import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LineProviderBadge } from '#/entities/telephony'
import {
  TEMPLATE_TYPE,
  useLoadTemplate,
  useTemplateOptions,
} from '#/entities/template'
import {
  insertFieldsToSlashGroups,
  relabelTemplateBody,
  TemplateComposeToolbar,
  useTemplateVariableOptions,
} from '#/entities/template-variable'
import { formatUsPhone } from '#/shared/lib/format'
import { useWorkspaceTabState } from '#/shared/lib/workspace-tabs'
import type { SmsDeliverability } from '#/shared/lib/sms-preview'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { ChevronDownIcon, CloseIcon, SendIcon } from '#/shared/ui/icons'
import {
  buildMassRecipientsLabels,
  MassRecipients,
} from '#/shared/ui/mass-recipients'
import { NoteEditor } from '#/shared/ui/note-editor'
import { Popover } from '#/shared/ui/popover'
import { sanitizeSmsFragment, sanitizeSmsHtml } from '#/shared/lib/sms-sanitize'
import {
  SmsCostPanel,
  SmsDeliverabilityNotice,
  SmsSanitizeNotice,
} from '#/shared/ui/sms-segment-counter'

import type { MassSmsFromNumber, MassSmsViewModel } from '../lib/mass-sms'
import { useMassSms } from '../model/use-mass-sms'
import type { UseMassSmsResult } from '../model/use-mass-sms'
import { MassSmsAttachments } from './mass-sms-attachments'

interface MassSmsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: () => void
  viewModel: MassSmsViewModel
  /**
   * Keep the typed message per workspace tab, so a tab switch that closes the
   * dialog brings the draft back with it. The global one-off composer opts
   * out: it is tab-independent and stays mounted across switches, so its
   * draft must not swap under the open editor.
   */
  perTabDraft?: boolean
}

function Header() {
  const { t } = useTranslation()
  return (
    <header className="flex shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-2.5">
      <DialogTitle className="text-base font-semibold text-gray-500">
        {t('massSms.title')}
      </DialogTitle>
      <DialogClose asChild>
        <button
          type="button"
          aria-label={t('massSms.close')}
          className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
          <CloseIcon className="size-3.5" />
        </button>
      </DialogClose>
    </header>
  )
}

function FromRow({
  fromNumbers,
  value,
  onChange,
}: {
  fromNumbers: MassSmsFromNumber[]
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const selected = fromNumbers.find((number) => number.id === value)
  return (
    <div className="flex h-9 shrink-0 items-center gap-2.5 border-b border-gray-200 px-2.5">
      <span className="text-xs text-gray-500 uppercase select-none">
        {t('massSms.from')}
      </span>
      <Popover
        align="left"
        portal
        trigger={(open) => (
          <span className="flex cursor-pointer items-center gap-1.25 text-sm text-gray-900">
            {selected
              ? formatUsPhone(selected.phoneNumber)
              : t('massSms.fromPlaceholder')}
            {selected ? (
              <LineProviderBadge isImessage={selected.isImessage} />
            ) : null}
            <ChevronDownIcon
              className={cn(
                'size-4 text-gray-500 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </span>
        )}
      >
        {(close) => (
          <div className="flex min-w-44 flex-col gap-0.5 rounded-md bg-white p-1 shadow-popover">
            {fromNumbers.length === 0 ? (
              <p className="px-2.5 py-1.5 text-sm text-gray-500">
                {t('massSms.noNumbers')}
              </p>
            ) : (
              fromNumbers.map((number) => (
                <button
                  key={number.id}
                  type="button"
                  onClick={() => {
                    onChange(number.id)
                    close()
                  }}
                  className="flex h-8 cursor-pointer items-center gap-1.5 rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100"
                >
                  {formatUsPhone(number.phoneNumber)}
                  <LineProviderBadge isImessage={number.isImessage} />
                </button>
              ))
            )}
          </div>
        )}
      </Popover>
    </div>
  )
}

function BottomMeta({
  chars,
  segments,
  willReceive,
  totalSms,
  message,
  plainText,
  deliverability,
  onMessageChange,
}: {
  chars: number
  segments: number
  willReceive: number
  totalSms: number
  message: string
  plainText: string
  deliverability: SmsDeliverability
  onMessageChange: (html: string) => void
}) {
  const { t } = useTranslation()
  return (
    <>
      {/* What the carrier is likely to do with this body — a blast is exactly
          where a spam-trigger word or a link shortener costs the most. */}
      <SmsDeliverabilityNotice
        findings={deliverability.findings}
        blockers={deliverability.blockers}
        className="mx-5 mt-2.5 shrink-0"
      />
      {/* Sits above the counter it explains: what is inflating the segment
          count, and one click to clear it — multiplied across every recipient,
          a stray emoji here is the difference between one SMS and two. */}
      <SmsSanitizeNotice html={message} className="mx-5 mt-2.5 shrink-0" />
      <SmsCostPanel
        html={message}
        plainText={plainText}
        onChange={onMessageChange}
        className="mx-5 mt-2.5 shrink-0"
      />
      <div className="flex shrink-0 items-center justify-between gap-2.5 px-5 py-2.5 text-xs">
        <p className="text-gray-500">
          {t('massSms.counter.chars', { chars, segments })}
        </p>
        <p className="font-medium text-gray-900">
          {t('massSms.counter.math', { willReceive, segments, totalSms })}
        </p>
      </div>
      <div className="shrink-0 border-t border-gray-200 px-5 py-2.5">
        <p className="text-xs text-gray-500">
          {t('massSms.carrierNote', { count: totalSms })}
        </p>
      </div>
    </>
  )
}

function Footer({
  willReceive,
  sendCount,
  canSend,
  isSending,
  onCancel,
  onSend,
}: {
  willReceive: number
  sendCount: number
  canSend: boolean
  isSending?: boolean
  onCancel: () => void
  onSend: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-14 shrink-0 items-center justify-between gap-2.5 border-t border-gray-200 py-2.5 pr-2.5 pl-5">
      <div className="flex items-center gap-1.75">
        <span className="size-1.75 shrink-0 rounded-full bg-green-500" />
        <p className="text-sm text-gray-500">
          {t('massSms.footer.summary', { willReceive, totalSms: sendCount })}
        </p>
      </div>
      <div className="flex items-center gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="px-3.5"
          onClick={onCancel}
        >
          {t('massSms.cancel')}
        </Button>
        <Button
          type="button"
          size="xs"
          pressEffect
          className="gap-2.5 px-3.75"
          disabled={!canSend || isSending}
          onClick={onSend}
        >
          <SendIcon className="size-3.5" />
          {t('massSms.send', { count: sendCount })}
        </Button>
      </div>
    </div>
  )
}

function ComposerSection({
  viewModel,
  sms,
}: {
  viewModel: MassSmsViewModel
  sms: UseMassSmsResult
}) {
  const { t } = useTranslation()
  const { options: templateOptions, isLoading: templateLoading } =
    useTemplateOptions({ templateType: TEMPLATE_TYPE.sms })
  const { groups: insertFieldGroups } = useTemplateVariableOptions()
  const slashGroups = useMemo(
    () => insertFieldsToSlashGroups(insertFieldGroups),
    [insertFieldGroups],
  )
  const loadTemplate = useLoadTemplate()
  // An iMessage (Speko) line is text-only — the backend silently drops
  // media_urls, so the media section disappears while one is selected.
  const fromImessage =
    viewModel.fromNumbers.find((number) => number.id === sms.fromNumberId)
      ?.isImessage ?? false
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <FromRow
        fromNumbers={viewModel.fromNumbers}
        value={sms.fromNumberId}
        onChange={sms.setFromNumberId}
      />
      <MassRecipients
        totalSelected={sms.totalSelected}
        willReceive={sms.willReceive}
        removed={sms.removed}
        expanded={sms.expandedRemoved}
        onToggle={sms.toggleRemoved}
        labels={buildMassRecipientsLabels(t, 'massSms', {
          totalSelected: sms.totalSelected,
          willReceive: sms.willReceive,
          removedCount: sms.removedCount,
        })}
      />
      <TemplateComposeToolbar
        templateOptions={templateOptions}
        templateLoading={templateLoading}
        onTemplateSelect={(id) =>
          void loadTemplate(id).then((template) =>
            sms.setMessage(
              relabelTemplateBody(template.body, insertFieldGroups),
            ),
          )
        }
      />
      {/* SMS sends as plain text — no formatting toolbar, matching the
          conversation composer. */}
      <NoteEditor
        value={sms.message}
        onChange={sms.setMessage}
        controllerRef={sms.editorRef}
        minHeight={250}
        placeholder={t('massSms.messagePlaceholder')}
        slashGroups={slashGroups}
        transformPastedText={sanitizeSmsFragment}
        transformPastedHTML={sanitizeSmsHtml}
        enableVoice
        hideToolbar
      />
      {!fromImessage && (
        <MassSmsAttachments
          attachments={sms.attachments}
          onAdd={sms.addFiles}
          onRemove={sms.removeAttachment}
          disabled={sms.attachmentsFull || sms.isSending}
        />
      )}
    </div>
  )
}

function MassSmsDialogBody({
  onOpenChange,
  onSent,
  viewModel,
  draft,
}: Omit<MassSmsDialogProps, 'open' | 'perTabDraft'> & {
  draft: [string, (value: string) => void]
}) {
  const sms = useMassSms(viewModel, draft)
  const handleSend = async () => {
    if (await sms.send()) {
      onSent?.()
      onOpenChange(false)
    }
  }
  return (
    <DialogContent className="max-w-xl">
      <Header />
      <ComposerSection viewModel={viewModel} sms={sms} />
      <BottomMeta
        chars={sms.chars}
        segments={sms.segments}
        willReceive={sms.willReceive}
        totalSms={sms.totalSms}
        message={sms.message}
        plainText={sms.plainText}
        deliverability={sms.deliverability}
        onMessageChange={sms.setMessage}
      />
      <Footer
        willReceive={sms.willReceive}
        sendCount={sms.sendCount}
        canSend={sms.canSend}
        isSending={sms.isSending}
        onCancel={() => onOpenChange(false)}
        onSend={() => void handleSend()}
      />
    </DialogContent>
  )
}

export function MassSmsDialog(props: MassSmsDialogProps) {
  const perTab = useWorkspaceTabState('massSmsDraft', '')
  const local = useState('')
  const draft = props.perTabDraft === false ? local : perTab
  const onOpenChange = (open: boolean) => {
    // An explicit close discards the draft. A workspace-tab switch never goes
    // through here — the other tab simply reads its own per-tab state.
    if (!open) draft[1]('')
    props.onOpenChange(open)
  }
  return (
    <Dialog open={props.open} onOpenChange={onOpenChange}>
      {props.open && (
        <MassSmsDialogBody
          {...props}
          onOpenChange={onOpenChange}
          draft={draft}
        />
      )}
    </Dialog>
  )
}
